import React from "react";
import moment from "moment";
import ReactTable from "react-table";
import "react-table/react-table.css";
import matchSorter from "match-sorter";
import "./ReporteVentasSire.css";

const QUERY_DELAY_MS = 2000;
const primaryColor = "rgb(53, 96, 90)";
const secondaryColor = "rgb(1, 165, 156)";
const lightColor = "rgba(53, 96, 90, 0.08)";

class ReporteVentasSire extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            periodoVentas: moment().format("YYYYMM"),
            loadingVentas: false,
            errorVentas: "",
            ventas: [],
            totalVentas: 0,
        };
    }

    componentDidMount() {
        this.consultarVentas();
    }

    monthToPeriodo = (monthValue) => {
        if (!monthValue || monthValue.length !== 7) return "";
        return monthValue.replace("-", "");
    }

    periodoToMonth = (periodo) => {
        if (!periodo || String(periodo).length !== 6) return "";
        return `${String(periodo).slice(0, 4)}-${String(periodo).slice(4, 6)}`;
    }

    fetchSireVentas = async (periodo) => {
        const response = await fetch(`/api/sire/sunat/ventas/${periodo}`);
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            const msg = data && data.msg ? data.msg : "Error consultando ventas";
            throw new Error(msg);
        }

        return data;
    }

    consultarVentas = async () => {
        const { periodoVentas } = this.state;

        if (!/^\d{6}$/.test(periodoVentas)) {
            this.setState({ errorVentas: "Periodo de ventas invalido. Formato: YYYYMM" });
            return;
        }

        this.setState({ loadingVentas: true, errorVentas: "" });

        try {
            await new Promise(resolve => setTimeout(resolve, QUERY_DELAY_MS));
            const data = await this.fetchSireVentas(periodoVentas);

            this.setState({
                ventas: Array.isArray(data.data) ? data.data : [],
                totalVentas: Number(data.total_registros || 0),
                loadingVentas: false,
            });
        } catch (error) {
            this.setState({
                loadingVentas: false,
                errorVentas: error.message || "No se pudo consultar ventas",
                ventas: [],
                totalVentas: 0,
            });
        }
    }

    formatMoney = (value, currency) => {
        const safeCurrency = currency === "USD" ? "USD" : "PEN";
        const num = Number(value || 0);

        return new Intl.NumberFormat("es-PE", {
            style: "currency",
            currency: safeCurrency,
            minimumFractionDigits: 2,
        }).format(num);
    }

    getResumenVentas = () => {
        const { ventas } = this.state;

        const totalRegistros = ventas.length;
        const totalPEN = ventas
            .filter(item => item.moneda === "PEN")
            .reduce((acc, item) => acc + Number(item.total || 0), 0);
        const totalUSD = ventas
            .filter(item => item.moneda === "USD")
            .reduce((acc, item) => acc + Number(item.total || 0), 0);
        const totalMonto = totalPEN + totalUSD;
        const promedio = totalRegistros ? totalMonto / totalRegistros : 0;

        const clientesMap = new Map();
        ventas.forEach(item => {
            const key = item.ruc_cliente || item.razon_social || "SIN-DATO";
            const actual = clientesMap.get(key) || {
                ruc: item.ruc_cliente,
                razon_social: item.razon_social,
                cantidad: 0,
                monto: 0,
            };
            actual.cantidad += 1;
            actual.monto += Number(item.total || 0);
            clientesMap.set(key, actual);
        });

        const clientes = Array.from(clientesMap.values());
        const topCliente = clientes.length
            ? clientes.sort((a, b) => b.monto - a.monto)[0]
            : null;

        return {
            totalRegistros,
            totalPEN,
            totalUSD,
            promedio,
            clientesUnicos: clientes.length,
            topCliente,
        };
    }

    renderCardsResumen = () => {
        const resumen = this.getResumenVentas();

        const cardStyle = {
            border: `1px solid ${lightColor}`,
            borderLeft: `5px solid ${secondaryColor}`,
            borderRadius: "10px",
            boxShadow: "0 3px 10px rgba(0,0,0,0.06)",
            background: "transparent",
        };

        const titleStyle = {
            color: primaryColor,
            fontSize: "0.8rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.3px",
        };

        const valueStyle = {
            color: secondaryColor,
            fontSize: "1.25rem",
            fontWeight: 700,
            marginBottom: 0,
        };

        const infoIconStyle = {
            display: "inline-block",
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            backgroundColor: secondaryColor,
            color: "white",
            fontSize: "0.7rem",
            lineHeight: "16px",
            textAlign: "center",
            marginLeft: "5px",
            cursor: "help",
        };

        return (
            <div className="row mb-3">
                <div className="col-md-3 col-sm-6 mb-3">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-body py-2">
                            <div style={titleStyle}>
                                Total Registros
                                <span style={infoIconStyle} title="Cantidad de comprobantes que usted ha declarado ante SUNAT">ℹ️</span>
                            </div>
                            <p style={valueStyle}>{resumen.totalRegistros}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 col-sm-6 mb-3">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-body py-2">
                            <div style={titleStyle}>
                                Monto Total (PEN)
                                <span style={infoIconStyle} title="Suma total en soles según su declaración en SUNAT - SIRE">ℹ️</span>
                            </div>
                            <p style={valueStyle}>{this.formatMoney(resumen.totalPEN, "PEN")}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 col-sm-6 mb-3">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-body py-2">
                            <div style={titleStyle}>
                                Monto Total (USD)
                                <span style={infoIconStyle} title="Suma total en dólares según su declaración en SUNAT - SIRE">ℹ️</span>
                            </div>
                            <p style={valueStyle}>{this.formatMoney(resumen.totalUSD, "USD")}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 col-sm-6 mb-3">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-body py-2">
                            <div style={titleStyle}>
                                Promedio por Comprobante
                                <span style={infoIconStyle} title="Monto promedio de los comprobantes declarados">ℹ️</span>
                            </div>
                            <p style={valueStyle}>{this.formatMoney(resumen.promedio, "PEN")}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-6 mb-3">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-body py-2">
                            <div style={titleStyle}>
                                Clientes Únicos
                                <span style={infoIconStyle} title="Cantidad de clientes diferentes en las ventas declaradas">ℹ️</span>
                            </div>
                            <p style={valueStyle}>{resumen.clientesUnicos}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-6 mb-3">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-body py-2">
                            <div style={titleStyle}>
                                Cliente con Mayor Monto
                                <span style={infoIconStyle} title="Cliente con mayor monto acumulado en su declaración">ℹ️</span>
                            </div>
                            <div className="font-weight-bold" style={{ color: primaryColor }}>
                                {resumen.topCliente ? (resumen.topCliente.razon_social || "SIN NOMBRE") : "Sin datos"}
                            </div>
                            <div className="small" style={{ color: secondaryColor }}>
                                {resumen.topCliente ? `${resumen.topCliente.ruc || "SIN RUC"} - ${this.formatMoney(resumen.topCliente.monto, "PEN")}` : ""}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderVentasTable = () => {
        const { ventas } = this.state;

        const columns = [
            {
                columns: [
                    {
                        Header: "Fecha",
                        id: "fecha_emision",
                        accessor: d => d.fecha_emision,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["fecha_emision"] }),
                        filterAll: true,
                    },
                    {
                        Header: "Tipo",
                        id: "tipo_doc",
                        accessor: d => d.tipo_doc,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["tipo_doc"] }),
                        filterAll: true,
                    },
                    {
                        Header: "Serie",
                        id: "serie",
                        accessor: d => d.serie,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["serie"] }),
                        filterAll: true,
                    },
                    {
                        Header: "Numero",
                        id: "numero",
                        accessor: d => d.numero,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["numero"] }),
                        filterAll: true,
                    },
                    {
                        Header: "RUC Cliente",
                        id: "ruc_cliente",
                        width: 150,
                        accessor: d => d.ruc_cliente,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["ruc_cliente"] }),
                        filterAll: true,
                    },
                    {
                        Header: "Razon Social",
                        id: "razon_social",
                        accessor: d => d.razon_social,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["razon_social"] }),
                        filterAll: true,
                    },
                    {
                        Header: "Total",
                        id: "total",
                        accessor: d => Number(d.total || 0),
                        Cell: props => <div className="text-right">{this.formatMoney(props.original.total, props.original.moneda)}</div>,
                    },
                    {
                        Header: "Moneda",
                        id: "moneda",
                        accessor: d => d.moneda,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["moneda"] }),
                        filterAll: true,
                    }
                ]
            }
        ];

        if (!ventas.length) {
            return <div className="alert alert-light mb-0">No hay ventas para el periodo consultado.</div>;
        }

        return (
            <div className="card border-0 shadow-sm sire-transparent-card" style={{ backgroundColor: "transparent" }}>
                <div
                    className="card-header d-flex justify-content-between align-items-center"
                    style={{
                        background: `linear-gradient(90deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                        color: "#fff",
                    }}
                >
                    <span className="font-weight-bold">Detalle de Ventas SUNAT</span>
                </div>
                <div className="card-body p-2" style={{ backgroundColor: "transparent" }}>
                    <ReactTable
                        data={ventas}
                        filterable
                        columns={columns}
                        defaultPageSize={10}
                        className="-striped -highlight"
                        previousText="Anterior"
                        nextText="Siguiente"
                        loadingText="Cargando..."
                        noDataText="No se encontraron registros"
                        pageText="Pagina"
                        ofText="de"
                        rowsText="filas"
                        pageSizeOptions={[10, 20, 25, 50, 100]}
                        minRows={1}
                    />
                </div>
            </div>
        );
    }

    render() {
        const {
            loadingVentas,
            errorVentas,
            periodoVentas,
            totalVentas,
        } = this.state;

        const disclaimerStyle = {
            backgroundColor: "#fff3cd",
            borderLeft: "4px solid #ffc107",
            padding: "12px 15px",
            borderRadius: "4px",
            marginBottom: "20px",
            fontSize: "0.9rem",
            lineHeight: "1.5",
        };

        const aclaracionImportanteStyle = {
            backgroundColor: "#ffe6e6",
            border: "2px solid #dc3545",
            borderRadius: "8px",
            padding: "15px",
            marginBottom: "20px",
            lineHeight: "1.6",
        };

        const bannerStyle = {
            backgroundColor: "#e7f3ff",
            border: "2px solid #0066cc",
            borderRadius: "8px",
            padding: "15px",
            marginBottom: "20px",
            textAlign: "center",
            fontWeight: "bold",
            color: "#0066cc",
        };

        return (
            <>
                <div className="py-3 reporte-ventas-sire-root">
                    {/* Banner con origen de datos */}
                    <div style={bannerStyle}>
                        <span style={{ fontSize: "1.2rem", marginRight: "8px" }}>☁️</span>
                        DATOS PROVENIENTES DE SUNAT - CONSULTA DIRECTA A SIRE
                    </div>

                    <div className="mb-4 text-center">
                        <h4 className="text-muted mb-2">Reporte de Ventas - Consulta SUNAT SISTEMA INTEGRADO DE REGISTROS ELECTRONICOS (SIRE)</h4>
                        <p className="text-muted mb-0">Información extraída directamente de los registros electrónicos que usted ha declarado ante SUNAT. Estos datos ARE la fuente de verdad para fines fiscales.</p>
                    </div>

                    {/* Aclaración Importante */}
                    <div style={aclaracionImportanteStyle}>
                        <h6 style={{ color: "#dc3545", marginBottom: "10px", fontWeight: "bold" }}>⚠️ ACLARACIÓN IMPORTANTE</h6>
                        <ul style={{ marginBottom: "0", paddingLeft: "20px" }}>
                            <li><strong>Estos datos corresponden a sus declaraciones ante SUNAT (SIRE).</strong> Representan lo que <strong>usted ha declarado</strong>, no necesariamente lo que está registrado en el sistema.</li>
                            <li>Posibles diferencias: ventas anuladas en SUNAT pero aún en sistema, fechas de comprobante diferentes a fechas de emisión real, ajustes por devoluciones no procesadas.</li>
                            <li>Use este reporte como <strong>fuente oficial para fines fiscales y de auditoría</strong>. Para reconciliar con movimientos internos, compare con el módulo de Ventas del sistema.</li>
                        </ul>
                    </div>

                    <div className="alert alert-info mb-4" role="alert">
                        <strong>ℹ️ Información Importante:</strong> Este reporte es solamente informativo y extraído de los registros de SIRE SUNAT por lo que no se debe de comparar con los registros hechos en el sistema, esta información es de carácter especial y de manejo contable.
                    </div>

                    <div className="card border-0 shadow-sm mb-4">
                        <div
                            className="card-header d-flex justify-content-between align-items-center"
                            style={{
                                background: `linear-gradient(90deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                                color: "#fff",
                            }}
                        >
                            <h5 className="mb-0">Ventas SUNAT</h5>
                            <span className="badge badge-light" style={{ color: primaryColor }}>{totalVentas} registros</span>
                        </div>
                        <div className="card-body">
                            <div className="form-row align-items-end mb-3">
                                <div className="col-md-3 mb-2 mb-md-0">
                                    <label className="mb-1">Periodo</label>
                                    <input
                                        type="month"
                                        className="form-control"
                                        value={this.periodoToMonth(periodoVentas)}
                                        onChange={(e) => this.setState({ periodoVentas: this.monthToPeriodo(e.target.value) })}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <button className="btn btn-primary btn-block" onClick={this.consultarVentas} disabled={loadingVentas}>
                                        {loadingVentas ? "Consultando..." : "Consultar SUNAT"}
                                    </button>
                                </div>
                            </div>

                            {errorVentas ? <div className="alert alert-danger">{errorVentas}</div> : null}
                            {loadingVentas ? <div className="text-muted"><strong>⏳ Obteniendo datos de SUNAT...</strong></div> : this.renderCardsResumen()}
                            {loadingVentas ? null : this.renderVentasTable()}
                        </div>
                    </div>

                    {/* Disclaimer al pie */}
                    <div style={disclaimerStyle}>
                        <strong>📌 Nota:</strong> La información mostrada corresponde exclusivamente a los registros electrónicos que usted ha declarado ante SUNAT (SIRE). Para ver sus movimientos internos de ventas y realizar comparativos, diríjase al módulo de Ventas del sistema.
                    </div>
                </div>
            </>
        );
    }
}

export default ReporteVentasSire;
